import React, { useCallback, useEffect, useMemo, useState } from "react";

import {
  TrendingUp,
  IndianRupee,
  Target,
  Trophy,
  Filter,
  RefreshCw,
} from "lucide-react";

import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";

import { motion } from "framer-motion";

import api from "../../common/apiClient";
import SummaryApi from "../../common/SummaryApi";
import MISFilterDrawer from "../MISFilterDrawer";


const RevenueForecastSkeleton = () => {
  return (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm animate-pulse">
      <div className="flex items-center justify-between p-5 border-b">
        <div className="h-6 w-40 bg-gray-200 rounded" />

        <div className="flex gap-2">
          <div className="h-9 w-9 bg-gray-200 rounded-lg" />

          <div className="h-9 w-9 bg-gray-200 rounded-lg" />
        </div>
      </div>

      <div className="p-5">
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
          {Array.from({ length: 4 }).map((_, index) => (
            <div key={index} className="h-24 bg-gray-100 rounded-xl" />
          ))}
        </div>

        <div className="h-[350px] bg-gray-100 rounded-xl" />

        <div className="mt-6 h-24 bg-gray-100 rounded-xl" />
      </div>
    </div>
  );
};

const RevenueForecastError = ({ onRetry }) => {
  return (
    <div className="bg-white rounded-2xl border border-red-200 p-6 text-center">
      <h3 className="text-lg font-semibold text-red-600">
        Failed to load Revenue Forecast
      </h3>

      <p className="text-sm text-gray-500 mt-2">
        Something went wrong while loading the report.
      </p>

      <button
        onClick={onRetry}
        className="mt-4 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg"
      >
        Retry
      </button>
    </div>
  );
};

const RevenueForecastCard = () => {
  const [loading, setLoading] = useState(true);

  const [error, setError] = useState(false);

  const [data, setData] = useState(null);

  const [showFilters, setShowFilters] = useState(false);

  const [filters, setFilters] = useState({
    filterType: "thisMonth",
    divisionId: "",
    userId: "",
    stageId: "",
    industryType: "",
    fromDate: "",
    toDate: "",
  });

  const [appliedFilters, setAppliedFilters] = useState({
    filterType: "thisMonth",
    divisionId: "",
    userId: "",
    stageId: "",
    industryType: "",
    fromDate: "",
    toDate: "",
  });

  const fetchReport = useCallback(async () => {
    try {
      setLoading(true);
      setError(false);

      const params = new URLSearchParams();

      Object.entries(appliedFilters).forEach(([key, value]) => {
        if (value !== "" && value !== null && value !== undefined) {
          params.append(key, value);
        }
      });

      const response = await api.get(
        `${SummaryApi.getRevenueForecast.url}?${params.toString()}`,
      );

      if (response?.data?.success) {
        setData(response.data.data);
      }
    } catch (err) {
      console.error(err);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [appliedFilters]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const topStage = useMemo(() => {
    if (!data?.chart?.length) return null;

    return [...data.chart].sort((a, b) => b.forecast - a.forecast)[0];
  }, [data]);

  if (loading) {
    return <RevenueForecastSkeleton />;
  }

  if (error) {
    return <RevenueForecastError onRetry={fetchReport} />;
  }

  const summary = data?.summary || {};

  const stats = [
    {
      title: "Pipeline Value",
      value: `₹${(summary.pipelineValue || 0).toLocaleString("en-IN")}`,
      icon: IndianRupee,
      bg: "bg-red-50",
      color: "text-red-600",
    },
    {
      title: "Forecast Revenue",
      value: `₹${(summary.forecastRevenue || 0).toLocaleString("en-IN")}`,
      icon: TrendingUp,
      bg: "bg-green-50",
      color: "text-green-600",
    },
    {
      title: "Forecast %",
      value: `${summary.forecastPercentage || 0}%`,
      icon: Target,
      bg: "bg-orange-50",
      color: "text-orange-600",
    },
    {
      title: "Won Revenue",
      value: `₹${(summary.wonRevenue || 0).toLocaleString("en-IN")}`,
      icon: Trophy,
      bg: "bg-blue-50",
      color: "text-blue-600",
    },
  ];

  return (
    <>
      <motion.div
        initial={{
          opacity: 0,
          y: 20,
        }}
        whileInView={{
          opacity: 1,
          y: 0,
        }}
        transition={{
          duration: 0.4,
        }}
        viewport={{
          once: true,
        }}
        className="bg-white rounded-2xl border border-gray-200 shadow-sm"
      >
        <div className="flex items-center justify-between p-5 border-b">
          <div className="flex items-center gap-2">
            <TrendingUp size={18} className="text-red-600" />

            <h2 className="font-semibold text-lg">Revenue Forecast</h2>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={fetchReport}
              className="h-9 w-9 rounded-lg border flex items-center justify-center"
            >
              <RefreshCw size={15} />
            </button>

            <button
              onClick={() => setShowFilters(true)}
              className="h-9 w-9 rounded-lg bg-red-600 text-white flex items-center justify-center"
            >
              <Filter size={15} />
            </button>
          </div>
        </div>

        <div className="p-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
            {stats.map((item) => {
              const Icon = item.icon;

              return (
                <div key={item.title} className={`${item.bg} rounded-xl p-4`}>
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-xs text-gray-500">{item.title}</p>

                      <h3 className={`mt-2 text-xl font-bold ${item.color}`}>
                        {item.value}
                      </h3>
                    </div>

                    <Icon size={20} className={item.color} />
                  </div>
                </div>
              );
            })}
          </div>

          <div className="h-[350px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.chart}>
                <CartesianGrid strokeDasharray="3 3" />

                <XAxis dataKey="stageName" />

                <YAxis />

                <Tooltip />

                <Legend />

                <Bar
                  dataKey="value"
                  name="Pipeline Value"
                  radius={[6, 6, 0, 0]}
                  fill="#dc2626"
                />

                <Bar
                  dataKey="forecast"
                  name="Forecast Revenue"
                  radius={[6, 6, 0, 0]}
                  fill="#16a34a"
                />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {topStage && (
            <div className="mt-6 rounded-xl border border-green-200 bg-green-50 p-4">
              <p className="text-sm text-green-700">Highest Forecast Stage</p>

              <h4 className="font-semibold text-green-800 mt-1">
                {topStage.stageName}
              </h4>

              <div className="mt-2 text-sm text-gray-600">
                Pipeline: ₹{topStage.value.toLocaleString("en-IN")}
                {" • "}
                Forecast: ₹{topStage.forecast.toLocaleString("en-IN")}
                {" • "}
                Probability:
                {topStage.probability}%
              </div>
            </div>
          )}
        </div>
      </motion.div>

      <MISFilterDrawer
        open={showFilters}
        onClose={() => setShowFilters(false)}
        filters={filters}
        setFilters={setFilters}
        onApply={() => {
          setAppliedFilters(filters);

          setShowFilters(false);
        }}
      />
    </>
  );
};

export default RevenueForecastCard;
